import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Play, Pause, Headphones, RotateCcw, Volume2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContentI18n } from "@/context/ContentI18n";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { ShareButton } from "@/components/ShareButton";
import { fmtTime } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resumeSec(id) {
  return parseFloat(localStorage.getItem(`sk_pos_${id}`) || "0");
}

export default function BookDetail() {
  const { id } = useParams();
  const { t, lang, fontClass } = useLang();
  const { title: ctitle, text: ctext } = useContentI18n();
  const { current, isPlaying, playIndex, togglePlay } = usePlayer();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/books/${id}`).then((r) => { if (live) setBook(r.data); })
      .catch(() => {}).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id]);

  const chapters = book?.chapters || [];

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <Link to={book?.category === "kids" ? "/rooms/story-kamare" : "/rooms/poth-kamare"} data-testid="book-back-link" className="inline-flex items-center gap-2 font-ui font-bold text-sm" style={{ color: "var(--accent)" }}>
          <ArrowLeft size={18} /> {book?.category === "kids" ? t("st_nav") : t("pk_nav")}
        </Link>
      </div>

      {loading ? (
        <div className="max-w-4xl mx-auto px-6 py-20 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("loading")}</div>
      ) : !book ? (
        <div className="max-w-4xl mx-auto px-6 py-20 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("pk_empty")}</div>
      ) : (
        <>
          <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <img src={book.cover_url} alt={book.title_en} className="w-40 h-40 sm:w-56 sm:h-56 rounded-3xl object-cover shrink-0 shadow-lg" style={{ border: "1px solid var(--border)" }} />
              <div className="min-w-0 flex-1">
                <h1 className={`${fontClass()} text-2xl sm:text-4xl font-bold leading-tight`} style={{ color: "var(--text)" }}>
                  {ctitle(book)}
                </h1>
                <p className="font-ui text-base font-semibold mt-2" style={{ color: "var(--accent)" }}>
                  {t("pk_by")}: {ctext(book.author)}
                </p>
                {(book.description_si || book.description_en) && (
                  <p className={`${fontClass()} text-base leading-relaxed mt-3`} style={{ color: "var(--text-2)" }}>
                    {lang === "si" ? (book.description_si || book.description_en) : (book.description_en || book.description_si)}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-3 mt-5">
                  <button
                    onClick={() => chapters.length && playIndex(0, chapters)}
                    disabled={!chapters.length}
                    data-testid="book-play-all-button"
                    className="inline-flex items-center gap-2 h-13 px-7 py-3 rounded-full font-ui text-base font-bold transition-transform active:scale-95 hover:scale-105 disabled:opacity-50"
                    style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}
                  >
                    <Play size={22} fill="currentColor" /> {t("pk_play_book")}
                  </button>
                  <span className="inline-flex items-center gap-2 font-ui text-sm font-bold px-4 py-2 rounded-full" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
                    <Headphones size={16} /> {chapters.length} {t("pk_chapters")}
                  </span>
                </div>
                <div className="mt-4">
                  <ShareButton testid="book-share" text={`📖 "${ctitle(book)}" — ${t("pk_nav")}`} />
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
            <h2 className={`${fontClass()} text-xl sm:text-2xl font-bold mb-4`} style={{ color: "var(--text)" }}>{t("pk_chapters")}</h2>
            <div className="space-y-3">
              {chapters.map((ch, i) => {
                const active = current && current.id === ch.id;
                const saved = resumeSec(ch.id);
                const hasResume = !active && saved > 5;
                return (
                  <div
                    key={ch.id}
                    data-testid={`chapter-row-${ch.id}`}
                    className="flex items-center gap-4 p-3 sm:p-4 rounded-2xl transition-all"
                    style={{ background: active ? "var(--card-hover)" : "var(--card)", border: `2px solid ${active ? "var(--accent-glow)" : "var(--border)"}` }}
                  >
                    <span className="grid place-items-center w-11 h-11 rounded-full shrink-0 font-ui font-bold" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
                      {active && isPlaying ? <Volume2 size={18} /> : i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`${fontClass()} text-base sm:text-lg font-bold truncate`} style={{ color: "var(--text)" }}>
                        {ctitle(ch)}
                      </p>
                      <p className="font-ui text-sm mt-0.5 flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
                        {t("pk_chapter")} {i + 1} · {fmtTime(ch.duration_sec)}
                        {hasResume && (
                          <span className="inline-flex items-center gap-1 font-bold" style={{ color: "var(--accent)" }}>
                            <RotateCcw size={13} /> {t("pk_resume")} {fmtTime(saved)}
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => (active ? togglePlay() : playIndex(i, chapters))}
                      data-testid={`chapter-play-button-${ch.id}`}
                      aria-label={`${active && isPlaying ? "Pause" : "Play"} chapter ${i + 1}`}
                      className="shrink-0 grid place-items-center rounded-full transition-transform active:scale-90 hover:scale-105"
                      style={{ width: 52, height: 52, background: active && isPlaying ? "var(--accent)" : "var(--player-bg)", color: "var(--player-accent)" }}
                    >
                      {active && isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" style={{ marginLeft: 2 }} />}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
}
